import { Anchor, Container, List, Stack, Text, Title } from '@mantine/core';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { marketingRouteMeta } from '#marketing/lib/marketing-route-meta.js';
import { localePath } from '#marketing/lib/marketing-locale.js';
import { useMarketingLocale } from '#marketing/lib/use-marketing-locale.js';

const SUPPORT_EMAIL = 'support@pachub.ca';
const TURNSTILE_PRIVACY_URL = 'https://www.cloudflare.com/turnstile-privacy-policy/';

/** @param {{ matches: Array<{ id?: string, pathname?: string, data?: unknown }> }} args */
export function meta({ matches }) {
  return marketingRouteMeta({ matches, page: 'privacy', pagePath: '/privacy' });
}

export default function Privacy() {
  const { t } = useTranslation('common');
  const locale = useMarketingLocale();
  const emailLink = <Anchor href={`mailto:${SUPPORT_EMAIL}`} />;

  return (
    <Container size="sm" py="xl">
      <Stack gap="md">
        <Title order={1}>{t('marketing.privacy.title')}</Title>
        <Text c="dimmed">{t('marketing.privacy.lastUpdated')}</Text>

        <Text>{t('marketing.privacy.intro')}</Text>

        <Title order={2} size="h3">
          {t('marketing.privacy.whoWeAreTitle')}
        </Title>
        <Text>
          <Trans
            i18nKey="marketing.privacy.whoWeAreBody"
            values={{ email: SUPPORT_EMAIL }}
            components={{ emailLink }}
          />
        </Text>

        <Title order={2} size="h3">
          {t('marketing.privacy.whatWeCollectTitle')}
        </Title>
        <Text>{t('marketing.privacy.whatWeCollectIntro')}</Text>
        <List spacing="xs">
          <List.Item>{t('marketing.privacy.whatWeCollectItems.nameEmail')}</List.Item>
          <List.Item>{t('marketing.privacy.whatWeCollectItems.role')}</List.Item>
          <List.Item>{t('marketing.privacy.whatWeCollectItems.school')}</List.Item>
          <List.Item>{t('marketing.privacy.whatWeCollectItems.message')}</List.Item>
          <List.Item>{t('marketing.privacy.whatWeCollectItems.marketingOptIn')}</List.Item>
        </List>
        <Text>
          {t('marketing.privacy.turnstileBodyBefore')}{' '}
          <Anchor href={TURNSTILE_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            {t('marketing.privacy.turnstileLink')}
          </Anchor>{' '}
          {t('marketing.privacy.turnstileBodyAfter')}
        </Text>

        <Title order={2} size="h3">
          {t('marketing.privacy.whyWeCollectTitle')}
        </Title>
        <List spacing="xs">
          <List.Item>{t('marketing.privacy.whyWeCollectItems.respond')}</List.Item>
          <List.Item>{t('marketing.privacy.whyWeCollectItems.followUp')}</List.Item>
          <List.Item>{t('marketing.privacy.whyWeCollectItems.marketing')}</List.Item>
        </List>
        <Text>{t('marketing.privacy.noSelling')}</Text>

        <Title order={2} size="h3">
          {t('marketing.privacy.whereStoredTitle')}
        </Title>
        <Text>{t('marketing.privacy.whereStoredBody')}</Text>

        <Title order={2} size="h3">
          {t('marketing.privacy.retentionTitle')}
        </Title>
        <Text>{t('marketing.privacy.retentionBody')}</Text>

        <Title order={2} size="h3">
          {t('marketing.privacy.choicesTitle')}
        </Title>
        <List spacing="xs">
          <List.Item>
            <Trans
              i18nKey="marketing.privacy.choicesItems.access"
              values={{ email: SUPPORT_EMAIL }}
              components={{ emailLink }}
            />
          </List.Item>
          <List.Item>{t('marketing.privacy.choicesItems.unsubscribe')}</List.Item>
        </List>

        <Title order={2} size="h3">
          {t('marketing.privacy.changesTitle')}
        </Title>
        <Text>{t('marketing.privacy.changesBody')}</Text>

        <Text>
          <Link to={localePath(locale, '/contact')}>{t('marketing.privacy.contactLink')}</Link>
        </Text>
      </Stack>
    </Container>
  );
}
